import { c } from "react/compiler-runtime";
import "@sanity/types";
import { createContext, useContext, useReducer, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Button, Flex, Grid, Portal, Stack, Tab, TabList, Text, TextInput } from "@sanity/ui";
import { css, styled } from "styled-components";
import { Menu, MenuItem } from "@sanity/ui/menu";
import { Popover } from "@sanity/ui/popover";
import { BlockContentIcon } from "@sanity/icons/BlockContent";
import { CheckmarkCircleIcon } from "@sanity/icons/CheckmarkCircle";
import { CubeIcon } from "@sanity/icons/Cube";
import { ImageIcon } from "@sanity/icons/Image";
import { NumberIcon } from "@sanity/icons/Number";
import { StringIcon } from "@sanity/icons/String";
import { ThListIcon } from "@sanity/icons/ThList";
import "rxjs";
import { SearchIcon } from "@sanity/icons/Search";
import { ThLargeIcon } from "@sanity/icons/ThLarge";
import { UlistIcon } from "@sanity/icons/Ulist";
import { Tooltip } from "@sanity/ui/tooltip";
import { startCase } from "lodash-es";
import { isValidElementType } from "react-is";
function getNodeIcon(option) {
	if (!option) return /* @__PURE__ */ jsx(CubeIcon, {});
	if (option.type === "string") return /* @__PURE__ */ jsx(StringIcon, {});
	if (option.type === "number") return /* @__PURE__ */ jsx(NumberIcon, {});
	let { value: node } = option;
	if ("icon" in option && option.icon) return /* @__PURE__ */ jsx("div", { dangerouslySetInnerHTML: { __html: option.icon } });
	let { type } = node;
	if (type === "string") return /* @__PURE__ */ jsx(StringIcon, {});
	if (type === "boolean") return /* @__PURE__ */ jsx(CheckmarkCircleIcon, {});
	if (type === "number") return /* @__PURE__ */ jsx(NumberIcon, {});
	if (type === "array" || type === "union") return (Array.isArray(node.of) ? node.of : [node.of]).some((n) => "name" in n && n.name === "block") ? /* @__PURE__ */ jsx(BlockContentIcon, {}) : /* @__PURE__ */ jsx(ThListIcon, {});
	if (type === "object") {
		if (option.name === "image") return /* @__PURE__ */ jsx(ImageIcon, {});
		if (option.name === "block") return /* @__PURE__ */ jsx(StringIcon, {});
	}
	return /* @__PURE__ */ jsx(CubeIcon, {});
}
const scrollBlockStyles = css`
  overflow-y: scroll;
  overscroll-behavior: contain;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &:before {
    content: '';
    display: block;
    height: calc(100% + 1px);
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
  }
`, PortalContainer = styled.div`
  height: 100%;
  inset: 0;
  pointer-events: all;
  position: fixed;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  ${(props) => props.$blockScroll && scrollBlockStyles}
`, PortalBackground = styled.div`
  background: transparent;
  height: 100%;
  inset: 0;
  position: absolute;
  width: 100%;
`, PopoverPortal = (props) => {
	let $ = c(2), t0;
	return $[0] === props ? t0 = $[1] : (t0 = /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(PopoverBackground, { ...props }) }), $[0] = props, $[1] = t0), t0;
}, PopoverBackground = (props) => {
	let $ = c(7), { children, onDismiss, setBoundaryElement, blockScroll: t0 } = props, blockScroll = t0 === void 0 || t0, handleClick = _temp$2, t1;
	$[0] === onDismiss ? t1 = $[1] : (t1 = /* @__PURE__ */ jsx(PortalBackground, { onMouseDownCapture: () => {
		onDismiss?.();
	} }), $[0] = onDismiss, $[1] = t1);
	let t2;
	return $[2] !== blockScroll || $[3] !== children || $[4] !== setBoundaryElement || $[5] !== t1 ? (t2 = /* @__PURE__ */ jsxs(PortalContainer, {
		"data-sanity-overlay-element": !0,
		ref: setBoundaryElement,
		onClick: handleClick,
		$blockScroll: blockScroll,
		children: [t1, children]
	}), $[2] = blockScroll, $[3] = children, $[4] = setBoundaryElement, $[5] = t1, $[6] = t2) : t2 = $[6], t2;
};
function _temp$2(event) {
	event.stopPropagation();
}
function defineEvent(options) {
	return {
		type: "log",
		name: options.name,
		version: options.version,
		description: options.description,
		maxSampleRate: options.maxSampleRate,
		schema: void 0
	};
}
const events = {
	"Visual Editing Drag Sequence Completed": defineEvent({
		name: "Visual Editing Drag Sequence Completed",
		description: "An array is successfully reordered using drag and drop.",
		version: 1
	}),
	"Visual Editing Drag Minimap Enabled": defineEvent({
		name: "Visual Editing Drag Minimap Enabled",
		description: "The zoomed-out minimap view is enabled during a drag sequence.",
		version: 1
	}),
	"Visual Editing Context Menu Item Removed": defineEvent({
		name: "Visual Editing Context Menu Item Removed",
		description: "An item is removed using the Context Menu.",
		version: 1
	}),
	"Visual Editing Context Menu Item Duplicated": defineEvent({
		name: "Visual Editing Context Menu Item Duplicated",
		description: "An item is duplicated using the Context Menu.",
		version: 1
	}),
	"Visual Editing Context Menu Item Moved": defineEvent({
		name: "Visual Editing Context Menu Item Moved",
		description: "An item is moved using the Context Menu.",
		version: 1
	}),
	"Visual Editing Context Menu Item Inserted": defineEvent({
		name: "Visual Editing Context Menu Item Inserted",
		description: "An item is inserted using the Context Menu.",
		version: 1
	}),
	"Visual Editing Insert Menu Item Inserted": defineEvent({
		name: "Visual Editing Insert Menu Item Inserted",
		description: "An item is inserted using the Insert Menu.",
		version: 1
	}),
	"Visual Editing Overlay Clicked": defineEvent({
		name: "Visual Editing Overlay Clicked",
		description: "An Overlay is clicked.",
		version: 1
	})
}, TelemetryContext = createContext(void 0);
function useTelemetry() {
	let context = useContext(TelemetryContext);
	if (!context) throw Error("Telemetry context is missing");
	return context;
}
/** @internal */
function getSchemaTypeIcon(schemaType) {
	let referenceIcon = isReferenceSchemaType(schemaType) && (schemaType.to ?? []).length === 1 ? schemaType.to[0].icon : void 0;
	return schemaType.icon ?? schemaType.type?.icon ?? referenceIcon;
}
function isReferenceSchemaType(type) {
	return isRecord(type) && (type.name === "reference" || isReferenceSchemaType(type.type));
}
function isRecord(value) {
	return !!value && (typeof value == "object" || typeof value == "function");
}
/**
* Vendored from the former `@sanity/insert-menu` package. A studio-native
* variant of this module lives in the `sanity` package
* (`packages/sanity/src/insert-menu`) and must be kept in sync with it.
*/
function fullInsertMenuReducer(state, event) {
	return {
		query: event.type === "change query" ? event.query : state.query,
		groups: event.type === "select group" ? state.groups.map((group) => ({
			...group,
			selected: event.name === group.name
		})) : state.groups,
		views: event.type === "toggle view" ? state.views.map((view) => ({
			...view,
			selected: event.name === view.name
		})) : state.views
	};
}
const ALL_ITEMS_GROUP_NAME = "all-items", gridStyle = {
	gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))",
	alignItems: "start"
};
/** @alpha */
function InsertMenu$1(props) {
	let $ = c(54), showIcons = props.showIcons === void 0 || props.showIcons, showFilter = props.filter === void 0 || props.filter === "auto" ? props.schemaTypes.length > 5 : props.filter, t0;
	$[0] !== props.groups || $[1] !== props.labels ? (t0 = props.groups ? [{
		name: ALL_ITEMS_GROUP_NAME,
		title: props.labels["insert-menu.filter.all-items"],
		selected: !0
	}, ...props.groups.map(_temp$1)] : [], $[0] = props.groups, $[1] = props.labels, $[2] = t0) : t0 = $[2];
	let t1;
	$[3] === props.views ? t1 = $[4] : (t1 = props.views ?? [{ name: "list" }], $[3] = props.views, $[4] = t1);
	let t2;
	$[5] === t1 ? t2 = $[6] : (t2 = t1.map(_temp2$1), $[5] = t1, $[6] = t2);
	let t3;
	$[7] !== t0 || $[8] !== t2 ? (t3 = {
		query: "",
		groups: t0,
		views: t2
	}, $[7] = t0, $[8] = t2, $[9] = t3) : t3 = $[9];
	let [state, send] = useReducer(fullInsertMenuReducer, t3), T0, T1, T2, t4, t5, t6, t7, t8, t9;
	if ($[10] !== props || $[11] !== showFilter || $[12] !== showIcons || $[13] !== state.groups || $[14] !== state.query || $[15] !== state.views) {
		let filteredSchemaTypes = filterSchemaTypes(props.schemaTypes, state.query, state.groups), selectedView = state.views.find(_temp3$1), showingFilterOrViews = showFilter || state.views.length > 1, showingTabs = state.groups && state.groups.length > 0, showingAnyOptions = showingFilterOrViews || showingTabs;
		T2 = Menu, t9 = 0, T1 = Flex, t6 = "column", t7 = "fill";
		let t10;
		$[25] === showingAnyOptions ? t10 = $[26] : (t10 = showingAnyOptions ? {
			style: { borderBottom: "1px solid var(--card-border-color)" },
			paddingBottom: 1
		} : {}, $[25] = showingAnyOptions, $[26] = t10);
		let t11;
		$[27] !== props.labels || $[28] !== showFilter || $[29] !== showingFilterOrViews || $[30] !== state.query || $[31] !== state.views ? (t11 = showingFilterOrViews ? /* @__PURE__ */ jsxs(Flex, {
			flex: "none",
			align: "center",
			paddingTop: 1,
			paddingX: 1,
			gap: 1,
			children: [showFilter ? /* @__PURE__ */ jsx(Box, {
				flex: 1,
				children: /* @__PURE__ */ jsx(TextInput, {
					autoFocus: !0,
					border: !1,
					fontSize: 1,
					icon: SearchIcon,
					onChange: (event) => {
						send({
							type: "change query",
							query: event.target.value
						});
					},
					placeholder: props.labels["insert-menu.search.placeholder"],
					value: state.query
				})
			}) : null, state.views.length > 1 ? /* @__PURE__ */ jsx(Box, {
				flex: "none",
				children: /* @__PURE__ */ jsx(ViewToggle, {
					views: state.views,
					onToggle: (name) => {
						send({
							type: "toggle view",
							name
						});
					},
					labels: props.labels
				})
			}) : null]
		}) : null, $[27] = props.labels, $[28] = showFilter, $[29] = showingFilterOrViews, $[30] = state.query, $[31] = state.views, $[32] = t11) : t11 = $[32];
		let t12;
		$[33] !== showingTabs || $[34] !== state.groups ? (t12 = showingTabs ? /* @__PURE__ */ jsx(Box, {
			paddingTop: 1,
			paddingX: 1,
			children: /* @__PURE__ */ jsx(TabList, {
				gap: 1,
				children: state.groups.map((group_0) => /* @__PURE__ */ jsx(Tab, {
					id: `${group_0.name}-tab`,
					"aria-controls": `${group_0.name}-panel`,
					label: group_0.title ?? startCase(group_0.name),
					selected: group_0.selected,
					onClick: () => {
						send({
							type: "select group",
							name: group_0.name
						});
					}
				}, group_0.name))
			})
		}) : null, $[33] = showingTabs, $[34] = state.groups, $[35] = t12) : t12 = $[35], $[36] !== t10 || $[37] !== t11 || $[38] !== t12 ? (t8 = /* @__PURE__ */ jsxs(Box, {
			...t10,
			children: [t11, t12]
		}), $[36] = t10, $[37] = t11, $[38] = t12, $[39] = t8) : t8 = $[39], T0 = Box, t4 = 1, t5 = filteredSchemaTypes.length === 0 ? /* @__PURE__ */ jsx(Box, {
			padding: 2,
			children: /* @__PURE__ */ jsx(Text, {
				muted: !0,
				size: 1,
				children: props.labels["insert-menu.search.no-results"]
			})
		}) : selectedView ? selectedView.name === "grid" ? /* @__PURE__ */ jsx(Grid, {
			autoRows: "auto",
			flex: 1,
			gap: 1,
			style: gridStyle,
			children: filteredSchemaTypes.map((schemaType) => /* @__PURE__ */ jsx(GridMenuItem, {
				icon: showIcons ? getSchemaTypeIcon(schemaType) : void 0,
				onClick: () => {
					props.onSelect(schemaType);
				},
				previewImageUrl: selectedView.previewImageUrl?.(schemaType.name),
				schemaType
			}, schemaType.name))
		}) : /* @__PURE__ */ jsx(Stack, {
			flex: 1,
			gap: 1,
			children: filteredSchemaTypes.map((schemaType_0) => /* @__PURE__ */ jsx(MenuItem, {
				icon: showIcons ? getSchemaTypeIcon(schemaType_0) : void 0,
				onClick: () => {
					props.onSelect(schemaType_0);
				},
				text: schemaType_0.title ?? startCase(schemaType_0.name)
			}, schemaType_0.name))
		}) : null, $[10] = props, $[11] = showFilter, $[12] = showIcons, $[13] = state.groups, $[14] = state.query, $[15] = state.views, $[16] = T0, $[17] = T1, $[18] = T2, $[19] = t4, $[20] = t5, $[21] = t6, $[22] = t7, $[23] = t8, $[24] = t9;
	} else T0 = $[16], T1 = $[17], T2 = $[18], t4 = $[19], t5 = $[20], t6 = $[21], t7 = $[22], t8 = $[23], t9 = $[24];
	let t10;
	$[40] !== T0 || $[41] !== t4 || $[42] !== t5 ? (t10 = /* @__PURE__ */ jsx(T0, {
		padding: t4,
		children: t5
	}), $[40] = T0, $[41] = t4, $[42] = t5, $[43] = t10) : t10 = $[43];
	let t11;
	$[44] !== T1 || $[45] !== t10 || $[46] !== t6 || $[47] !== t7 || $[48] !== t8 ? (t11 = /* @__PURE__ */ jsxs(T1, {
		direction: t6,
		height: t7,
		children: [t8, t10]
	}), $[44] = T1, $[45] = t10, $[46] = t6, $[47] = t7, $[48] = t8, $[49] = t11) : t11 = $[49];
	let t12;
	return $[50] !== T2 || $[51] !== t11 || $[52] !== t9 ? (t12 = /* @__PURE__ */ jsx(T2, {
		padding: t9,
		children: t11
	}), $[50] = T2, $[51] = t11, $[52] = t9, $[53] = t12) : t12 = $[53], t12;
}
function _temp3$1(view_0) {
	return view_0.selected;
}
function _temp2$1(view, index) {
	return {
		...view,
		selected: index === 0
	};
}
function _temp$1(group) {
	return {
		...group,
		selected: !1
	};
}
const viewToggleIcon = {
	grid: ThLargeIcon,
	list: UlistIcon
}, viewToggleTooltip = {
	grid: "insert-menu.toggle-grid-view.tooltip",
	list: "insert-menu.toggle-list-view.tooltip"
};
function ViewToggle(props) {
	let $ = c(11), viewIndex = props.views.findIndex(_temp4), nextView = props.views[viewIndex + 1] ?? props.views[0], t0 = props.labels[viewToggleTooltip[nextView.name]], t1;
	$[0] === t0 ? t1 = $[1] : (t1 = /* @__PURE__ */ jsx(Text, {
		size: 1,
		children: t0
	}), $[0] = t0, $[1] = t1);
	let t2 = viewToggleIcon[nextView.name], t3;
	$[2] !== nextView.name || $[3] !== props ? (t3 = () => {
		props.onToggle(nextView.name);
	}, $[2] = nextView.name, $[3] = props, $[4] = t3) : t3 = $[4];
	let t4;
	$[5] !== t2 || $[6] !== t3 ? (t4 = /* @__PURE__ */ jsx(Button, {
		mode: "bleed",
		icon: t2,
		onClick: t3
	}), $[5] = t2, $[6] = t3, $[7] = t4) : t4 = $[7];
	let t5;
	return $[8] !== t1 || $[9] !== t4 ? (t5 = /* @__PURE__ */ jsx(Tooltip, {
		content: t1,
		placement: "top",
		portal: !0,
		children: t4
	}), $[8] = t1, $[9] = t4, $[10] = t5) : t5 = $[10], t5;
}
function _temp4(view) {
	return view.selected;
}
function GridMenuItem(props) {
	let $ = c(20), [failedToLoad, setFailedToLoad] = useState(!1), Icon = props.icon, hasPreviewImage = !!props.previewImageUrl && !failedToLoad, t0;
	$[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = { overflow: "hidden" }, $[0] = t0) : t0 = $[0];
	let t1;
	$[1] === Symbol.for("react.memo_cache_sentinel") ? (t1 = {
		backgroundColor: "var(--card-muted-bg-color)",
		paddingBottom: "66.6%",
		position: "relative"
	}, $[1] = t1) : t1 = $[1];
	let t2;
	$[2] !== Icon || $[3] !== hasPreviewImage ? (t2 = isValidElementType(Icon) && !hasPreviewImage ? /* @__PURE__ */ jsx(Flex, {
		align: "center",
		justify: "center",
		style: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%"
		},
		children: /* @__PURE__ */ jsx(Text, {
			size: 1,
			children: /* @__PURE__ */ jsx(Icon, {})
		})
	}) : null, $[2] = Icon, $[3] = hasPreviewImage, $[4] = t2) : t2 = $[4];
	let t3;
	$[5] !== hasPreviewImage || $[6] !== props.previewImageUrl ? (t3 = hasPreviewImage ? /* @__PURE__ */ jsx("img", {
		src: props.previewImageUrl,
		style: {
			objectFit: "contain",
			width: "100%",
			height: "100%",
			position: "absolute",
			inset: 0
		},
		onError: () => {
			setFailedToLoad(!0);
		}
	}) : null, $[5] = hasPreviewImage, $[6] = props.previewImageUrl, $[7] = t3) : t3 = $[7];
	let t4;
	$[8] === Symbol.for("react.memo_cache_sentinel") ? (t4 = /* @__PURE__ */ jsx("div", { style: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		boxShadow: "inset 0 0 0 0.5px var(--card-fg-color)",
		opacity: .1
	} }), $[8] = t4) : t4 = $[8];
	let t5;
	$[9] !== t2 || $[10] !== t3 ? (t5 = /* @__PURE__ */ jsxs(Box, {
		flex: "none",
		style: t1,
		children: [
			t2,
			t3,
			t4
		]
	}), $[9] = t2, $[10] = t3, $[11] = t5) : t5 = $[11];
	let t6 = props.schemaType.title ?? props.schemaType.name, t7;
	$[12] === t6 ? t7 = $[13] : (t7 = /* @__PURE__ */ jsx(Box, {
		flex: "none",
		paddingX: 2,
		paddingY: 1,
		children: /* @__PURE__ */ jsx(Text, {
			size: 1,
			weight: "medium",
			children: t6
		})
	}), $[12] = t6, $[13] = t7);
	let t8;
	$[14] !== t5 || $[15] !== t7 ? (t8 = /* @__PURE__ */ jsxs(Flex, {
		direction: "column",
		gap: 1,
		padding: 1,
		children: [t5, t7]
	}), $[14] = t5, $[15] = t7, $[16] = t8) : t8 = $[16];
	let t9;
	return $[17] !== props.onClick || $[18] !== t8 ? (t9 = /* @__PURE__ */ jsx(MenuItem, {
		padding: 0,
		radius: 2,
		onClick: props.onClick,
		style: t0,
		children: t8
	}), $[17] = props.onClick, $[18] = t8, $[19] = t9) : t9 = $[19], t9;
}
function filterSchemaTypes(schemaTypes, query, groups) {
	return schemaTypes.filter((schemaType) => passesGroupFilter(schemaType, groups) && passesQueryFilter(schemaType, query));
}
function passesQueryFilter(schemaType, query) {
	let sanitizedQuery = query.trim().toLowerCase();
	return schemaType.title ? schemaType.title?.toLowerCase().includes(sanitizedQuery) : schemaType.name.includes(sanitizedQuery);
}
function passesGroupFilter(schemaType, groups) {
	let selectedGroup = groups.find((group) => group.selected);
	return !selectedGroup || selectedGroup.name === ALL_ITEMS_GROUP_NAME || selectedGroup.of?.includes(schemaType.name);
}
const labels = {
	"insert-menu.filter.all-items": "All",
	"insert-menu.search.no-results": "No results",
	"insert-menu.search.placeholder": "Filter types…",
	"insert-menu.toggle-grid-view.tooltip": "Toggle grid view",
	"insert-menu.toggle-list-view.tooltip": "Toggle list view"
}, InsertMenu = (props) => {
	let $ = c(11), { node, onSelect } = props, t0;
	$[0] === node.options?.insertMenu ? t0 = $[1] : (t0 = node.options?.insertMenu || {}, $[0] = node.options?.insertMenu, $[1] = t0);
	let insertMenuOptions = t0, t1;
	$[2] === insertMenuOptions.views ? t1 = $[3] : (t1 = insertMenuOptions.views?.map(_temp), $[2] = insertMenuOptions.views, $[3] = t1);
	let views = t1, t2;
	$[4] === node.of ? t2 = $[5] : (t2 = node.of.map(_temp2), $[4] = node.of, $[5] = t2);
	let t3;
	return $[6] !== insertMenuOptions || $[7] !== onSelect || $[8] !== t2 || $[9] !== views ? (t3 = /* @__PURE__ */ jsx(InsertMenu$1, {
		...insertMenuOptions,
		labels,
		schemaTypes: t2,
		onSelect,
		views
	}), $[6] = insertMenuOptions, $[7] = onSelect, $[8] = t2, $[9] = views, $[10] = t3) : t3 = $[10], t3;
}, InsertMenuPopover = (props) => {
	let $ = c(11), { node, onDismiss, onSelect, referenceElement } = props, t0;
	$[0] !== node || $[1] !== onSelect ? (t0 = /* @__PURE__ */ jsx(InsertMenu, {
		node,
		onSelect
	}), $[0] = node, $[1] = onSelect, $[2] = t0) : t0 = $[2];
	let popoverContent = t0, width = (node.options?.insertMenu || {}).views?.some(_temp3) ? 0 : void 0, t1;
	$[3] === Symbol.for("react.memo_cache_sentinel") ? (t1 = ["bottom"], $[3] = t1) : t1 = $[3];
	let t2;
	$[4] !== popoverContent || $[5] !== referenceElement || $[6] !== width ? (t2 = /* @__PURE__ */ jsx(Popover, {
		animate: !0,
		constrainSize: !0,
		content: popoverContent,
		fallbackPlacements: t1,
		open: !0,
		placement: "top",
		preventOverflow: !0,
		referenceElement,
		width
	}), $[4] = popoverContent, $[5] = referenceElement, $[6] = width, $[7] = t2) : t2 = $[7];
	let t3;
	return $[8] !== onDismiss || $[9] !== t2 ? (t3 = /* @__PURE__ */ jsx(PopoverPortal, {
		onDismiss,
		children: t2
	}), $[8] = onDismiss, $[9] = t2, $[10] = t3) : t3 = $[10], t3;
};
function _temp(view) {
	return view.name === "grid" ? {
		...view,
		previewImageUrl: (name) => view.previewImageUrls?.[name]
	} : view;
}
function _temp2(type) {
	return {
		...type,
		icon: getNodeIcon(type)
	};
}
function _temp3(view) {
	return view.name === "grid";
}
const PointerEvents = (t0) => {
	let $ = c(10), children, rest, style;
	$[0] === t0 ? (children = $[1], rest = $[2], style = $[3]) : ({children, style, ...rest} = t0, $[0] = t0, $[1] = children, $[2] = rest, $[3] = style);
	let t1;
	$[4] === style ? t1 = $[5] : (t1 = {
		...style,
		pointerEvents: "all"
	}, $[4] = style, $[5] = t1);
	let t2;
	return $[6] !== children || $[7] !== rest || $[8] !== t1 ? (t2 = /* @__PURE__ */ jsx("div", {
		style: t1,
		"data-sanity-overlay-element": !0,
		...rest,
		children
	}), $[6] = children, $[7] = rest, $[8] = t1, $[9] = t2) : t2 = $[9], t2;
};
export { TelemetryContext as a, PopoverPortal as c, useTelemetry as i, getNodeIcon as l, InsertMenu as n, events as o, InsertMenuPopover as r, PopoverBackground as s, PointerEvents as t };

//# sourceMappingURL=PointerEvents-DYXUIXGG.js.map